import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { serviceImages } from '../../constants/images';
import Footer from '../Footer';

const ERPImplementation = () => {
  useEffect(() => {
    document.title = 'ERP Implementation - Global Excellence';
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: '📊',
      title: 'Business Analysis',
      description: 'Comprehensive business process analysis'
    },
    {
      icon: '🔄',
      title: 'System Design',
      description: 'Customized ERP system design'
    },
    {
      icon: '⚙️',
      title: 'Configuration',
      description: 'System setup and customization'
    },
    {
      icon: '📚',
      title: 'Training',
      description: 'User training and documentation'
    },
    {
      icon: '🔍',
      title: 'Testing',
      description: 'Thorough system testing'
    },
    {
      icon: '🚀',
      title: 'Deployment',
      description: 'Smooth system deployment'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discovery',
      description: 'Understand business requirements'
    },
    {
      number: '02',
      title: 'Planning',
      description: 'Create implementation roadmap'
    },
    {
      number: '03',
      title: 'Design',
      description: 'Design system architecture'
    },
    {
      number: '04',
      title: 'Development',
      description: 'Configure and customize system'
    },
    {
      number: '05',
      title: 'Testing',
      description: 'Validate system functionality'
    },
    {
      number: '06',
      title: 'Go-Live',
      description: 'Deploy and support system'
    }
  ];

  const technologies = [
    { icon: '📊', name: 'SAP' },
    { icon: '🔄', name: 'Oracle' },
    { icon: '💼', name: 'Microsoft' },
    { icon: '📈', name: 'NetSuite' },
    { icon: '🛠️', name: 'Workday' },
    { icon: '📱', name: 'Infor' },
    { icon: '🔍', name: 'Epicor' },
    { icon: '📦', name: 'Acumatica' }
  ];

  return (
    <>
    <div className="service-component-wrapper">
      {/* Hero Section */}
      <section className="service-component-hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="service-hero-content"
        >
          <h1 className="service-hero-title">ERP Implementation</h1>
          <p className="service-hero-subtitle">
            Streamline your business operations with expert ERP implementation
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="service-overview">
        <div className="service-overview-content">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="service-overview-text"
          >
            <h2>Comprehensive ERP Solutions</h2>
            <p>
              Our ERP implementation services help businesses optimize their operations
              through seamless integration of enterprise resource planning systems.
              We ensure smooth transitions and maximum ROI from your ERP investment.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="service-overview-image"
          >
            <img src={serviceImages.erpImplementation} alt="ERP Implementation" />
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="service-features">
        <h2>Our Implementation Services</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="feature-card"
            >
              <span className="feature-icon">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="service-process">
        <h2>Our Implementation Process</h2>
        <div className="process-grid">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="process-card"
            >
              <span className="process-number">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="service-technologies">
        <h2>ERP Systems We Implement</h2>
        <div className="tech-grid">
          {technologies.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="tech-item"
            >
              <span className="tech-icon">{tech.icon}</span>
              <span className="tech-name">{tech.name}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="cta-content"
        >
          <h2 style={{color: 'white'}}>Ready to Transform Your Business Operations?</h2>
          <p style={{color: 'white'}}>Let's discuss how we can help you implement the perfect ERP solution</p>
          <div className="cta-buttons">
            <Link to="/contactus" className="cta-button primary">
              Get Started
            </Link>
            <Link to="/services" className="cta-button secondary">
              View All Services
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default ERPImplementation; 